import React, { useState } from 'react';
import {
  Stack,
  Typography,
  MenuItem,
  Alert,
} from '@mui/material';
import { Replay as ReturnIcon } from '@mui/icons-material';
import {
  EasyCheckDialog,
  EasyCheckButton,
  EasyCheckTextField,
  EasyCheckSelect,
  EasyCheckDatePicker,
} from '../../common/Controls';
import { useMarkReturned } from '../../../hooks/useChequeCollections';

const RETURN_REASONS = [
  'Supplier rejected cheque',
  'Courier failed — address not found',
  'Courier failed — recipient unavailable',
  'Wrong beneficiary name',
  'Cheque expired or damaged',
  'Supplier requested reissuance',
  'Other',
];

const ReturnDialog = ({ open, cheque, onClose, onSuccess, onError }) => {
  const returnMutation = useMarkReturned();

  const [form, setForm] = useState({
    returnDate: new Date(),
    returnReason: '',
    returnNotes: '',
  });

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.returnReason) {
      onError('Return reason is required');
      return;
    }

    const collectionId = cheque.Id || cheque.id || 0;
    const chequeId = cheque.ChequeId || cheque.chequeId;
    try {
      const result = await returnMutation.mutateAsync({
        collectionId,
        chequeId,
        returnDate: form.returnDate?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
        returnReason: form.returnReason,
        returnNotes: form.returnNotes.trim() || null,
      });
      if (result.success) {
        onSuccess(result.message || 'Cheque marked as returned');
      } else {
        onError(result.message || 'Failed to mark as returned');
      }
    } catch (err) {
      onError('Failed to mark as returned');
    }
  };

  const chequeNumber = cheque?.ChequeNumber || cheque?.chequeNumber || '';
  const beneficiary = cheque?.BeneficiaryName || cheque?.beneficiaryName || '';

  return (
    <EasyCheckDialog
      open={open}
      onClose={onClose}
      title="Mark as Returned"
      variant="form"
      titleIcon={<ReturnIcon />}
      actions={
        <>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={onClose}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            color="error"
            onClick={handleSave}
            loading={returnMutation.isPending}
          >
            Mark Returned
          </EasyCheckButton>
        </>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cheque <strong>{chequeNumber}</strong> — {beneficiary}
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        This cheque will revert to <strong>Printed</strong> status and can be re-dispatched.
      </Alert>

      <Stack spacing={2}>
        <EasyCheckDatePicker
          label="Return Date"
          value={form.returnDate}
          onChange={handleChange('returnDate')}
          slotProps={{ textField: { required: true, fullWidth: true } }}
        />

        <EasyCheckSelect
          label="Return Reason"
          value={form.returnReason}
          onChange={handleChange('returnReason')}
          required
          fullWidth
        >
          {RETURN_REASONS.map((reason) => (
            <MenuItem key={reason} value={reason}>
              {reason}
            </MenuItem>
          ))}
        </EasyCheckSelect>

        <EasyCheckTextField
          label="Additional Notes"
          value={form.returnNotes}
          onChange={handleChange('returnNotes')}
          fullWidth
          multiline
          rows={3}
          placeholder="Additional details (optional)"
        />
      </Stack>
    </EasyCheckDialog>
  );
};

export default ReturnDialog;
